import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const blank = {
  seller_street: '', seller_city: '', seller_postal_code: '', seller_country: 'DE',
  seller_vat_id: '', seller_tax_number: '', iban: '', bic: '',
  buyer_street: '', buyer_city: '', buyer_postal_code: '', buyer_country: 'DE',
  buyer_vat_id: '', buyer_email: '', buyer_reference: '', default_vat_rate: '19',
};

export function EInvoiceExportModal({ open, onClose, invoice, client }) {
  const [form, setForm] = useState(blank);
  const [checking, setChecking] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    api.get('/account').then(({ data: account }) => setForm({
      ...blank,
      seller_street: account.company_address || '',
      seller_vat_id: account.gst_number || '',
      buyer_street: client?.address || '',
      buyer_city: client?.city || '',
      buyer_postal_code: client?.postal_code || '',
      buyer_email: client?.billing_email || client?.email || '',
      buyer_reference: invoice?.client_po_number || invoice?.client_ticket_number || '-',
    })).catch(() => toast.error('Could not load company information'));
  }, [open, invoice, client]);

  const payload = () => ({
    ...form,
    seller_vat_id: form.seller_vat_id || null,
    seller_tax_number: form.seller_tax_number || null,
    bic: form.bic || null,
    buyer_vat_id: form.buyer_vat_id || null,
    buyer_email: form.buyer_email || null,
    default_vat_rate: Number(form.default_vat_rate),
    line_taxes: [],
  });
  const change = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const validate = async () => {
    setChecking(true);
    try {
      const response = await api.post(`/einvoice/invoices/${invoice.id}/validate`, payload());
      setResult(response.data);
      if (response.data.ready && response.data.official_validation?.accepted) toast.success('Official validation passed');
      else if (response.data.ready) toast.success('Invoice data is ready; official validator must be configured before download');
      else toast.error('Some required e-invoice information is missing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'E-invoice validation failed');
    } finally { setChecking(false); }
  };

  const download = async (format) => {
    setDownloading(format);
    try {
      const response = await api.post(`/einvoice/invoices/${invoice.id}/${format}`, payload(), { responseType: 'blob' });
      const extension = format === 'xrechnung' ? 'xml' : 'pdf';
      const name = `${invoice.invoice_number}-${format === 'xrechnung' ? 'XRechnung' : 'Factur-X'}.${extension}`;
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = name; document.body.appendChild(anchor); anchor.click(); anchor.remove();
      URL.revokeObjectURL(url);
      toast.success(`${name} downloaded`);
    } catch (error) {
      let message = 'E-invoice download failed';
      if (error.response?.data instanceof Blob) {
        try { const body = JSON.parse(await error.response.data.text()); message = typeof body.detail === 'string' ? body.detail : body.detail?.error || message; } catch (_) {}
      }
      toast.error(message);
    } finally { setDownloading(''); }
  };

  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>German E-Invoice / Deutsche E-Rechnung</DialogTitle>
        </DialogHeader>
        <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
          These export details are used only for this download and are not saved in ServiceFlow.
          Existing Canadian/US data is not changed.
        </div>
        <div className="space-y-3">
          <h3 className="font-semibold">Seller / Rechnungssteller</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Street *</Label><Input value={form.seller_street} onChange={change('seller_street')} /></div>
            <div className="space-y-1"><Label>City *</Label><Input value={form.seller_city} onChange={change('seller_city')} /></div>
            <div className="space-y-1"><Label>Postal code *</Label><Input value={form.seller_postal_code} onChange={change('seller_postal_code')} /></div>
            <div className="space-y-1"><Label>Country code</Label><Input maxLength={2} value={form.seller_country} onChange={change('seller_country')} /></div>
            <div className="space-y-1"><Label>VAT ID / USt-IdNr.</Label><Input value={form.seller_vat_id} onChange={change('seller_vat_id')} /></div>
            <div className="space-y-1"><Label>Tax number / Steuernummer</Label><Input value={form.seller_tax_number} onChange={change('seller_tax_number')} /></div>
            <div className="space-y-1"><Label>IBAN *</Label><Input value={form.iban} onChange={change('iban')} /></div>
            <div className="space-y-1"><Label>BIC</Label><Input value={form.bic} onChange={change('bic')} /></div>
          </div>
          <h3 className="font-semibold pt-2">Buyer / Rechnungsempfänger</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Street *</Label><Input value={form.buyer_street} onChange={change('buyer_street')} /></div>
            <div className="space-y-1"><Label>City *</Label><Input value={form.buyer_city} onChange={change('buyer_city')} /></div>
            <div className="space-y-1"><Label>Postal code *</Label><Input value={form.buyer_postal_code} onChange={change('buyer_postal_code')} /></div>
            <div className="space-y-1"><Label>Country code</Label><Input maxLength={2} value={form.buyer_country} onChange={change('buyer_country')} /></div>
            <div className="space-y-1"><Label>VAT ID</Label><Input value={form.buyer_vat_id} onChange={change('buyer_vat_id')} /></div>
            <div className="space-y-1"><Label>Email *</Label><Input type="email" value={form.buyer_email} onChange={change('buyer_email')} /></div>
            <div className="space-y-1"><Label>Buyer reference / Leitweg-ID *</Label><Input value={form.buyer_reference} onChange={change('buyer_reference')} /></div>
            <div className="space-y-1"><Label>VAT rate / MwSt. %</Label><Input type="number" step="0.01" value={form.default_vat_rate} onChange={change('default_vat_rate')} /></div>
          </div>
        </div>
        {result && (
          <div className={`rounded-md border p-3 text-sm ${result.ready ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <p className="font-semibold">{result.ready ? 'Invoice data is complete.' : 'Please correct the following:'}</p>
            {(result.issues || []).map((issue, index) => <p key={index}>{issue.rule}: {issue.message}</p>)}
            {result.totals && <p className="mt-2">Net: €{result.totals.net_total} · VAT: €{result.totals.tax_total} · Total: €{result.totals.gross_total}</p>}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={validate} disabled={checking}>{checking ? 'Checking…' : 'Validate'}</Button>
          <Button onClick={() => download('xrechnung')} disabled={!!downloading}>{downloading === 'xrechnung' ? 'Generating…' : 'Download XRechnung'}</Button>
          <Button onClick={() => download('facturx')} disabled={!!downloading}>{downloading === 'facturx' ? 'Generating…' : 'Download Factur-X PDF'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
